import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { Link } from '~/lib/react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { toPromoBrand } from '~/consts/promoBrand';

import { useExpireVoucherUser } from '~/hooks/apis/promo/useExpireVoucherUser';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { allVoucherUsersQueryKeys, useListAllVoucherUsers } from '~/hooks/apis/promo/useListAllVoucherUsers';
import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import {
  ListAllVoucherUsersQuery,
  ListVoucherLogsQuery,
  PromoMeta,
  VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER,
  listVouchers,
} from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import CopyableField from '../Common/CopyableField';
import DebugModal from '../DebugModal/DebugModal';

import VoucherLogsTable, { VOUCHER_LOGS_TABLE_DEFAULT_QUERY } from './VoucherLogsTable';
import VoucherRecentRequests from './VoucherRecentRequests';

type Voucher = definitions['Voucher'];
type VoucherUser = definitions['VoucherUser'];
/** API fields not always on contract `VoucherUser` — order id when auto-assigned from an order. */
type VoucherUserRow = VoucherUser & { fkCauseId?: string; idCauseFk?: string };

type VoucherUserReportingTagDefinition = NonNullable<
  PromoMeta['global_promo_config']
>['voucherUserReportingTagsConfig'][number];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function reportingTagOptionsFromPromoMeta(
  config: Array<VoucherUserReportingTagDefinition> | undefined,
): Array<{ value: string; label: string }> {
  if (!config) {
    return [];
  }
  const fromMeta = config?.map((def) => ({
    value: def.tag,
    label: def.short_description?.trim() ? def.short_description : def.tag,
  }));
  return fromMeta;
}

function getReportingTagFilterOptions(
  metaOptions: Array<{ value: string; label: string }>,
  selectedTag: string,
): Array<{ value: string; label: string }> {
  const base = [{ value: '', label: 'All reporting tags' }, ...metaOptions];
  if (selectedTag && !base.some((o) => o.value === selectedTag)) {
    return [...base, { value: selectedTag, label: selectedTag }];
  }
  return base;
}

/**
 * URL `include_expired=true` enables; explicit `false` or other values disable.
 * When the param is omitted, uses `defaultWhenOmitted` (standalone voucher lookup uses false;
 * voucher edit embed uses true to preserve legacy always-show-expired behavior).
 */
function initialIncludeExpiredFromSearchParams(search: URLSearchParams, defaultWhenOmitted: boolean): boolean {
  const raw = search.get('include_expired');
  if (raw === 'true') {
    return true;
  }
  if (raw === null) {
    return defaultWhenOmitted;
  }
  return false;
}

/** URL `include_used=false` disables; omitted or other → true (matches list API default). */
function parseIncludeUsedFromSearchParams(search: URLSearchParams): boolean {
  return search.get('include_used') !== 'false';
}

/** URL `only_used=true` enables; omitted or other → false (matches list API default). */
function parseOnlyUsedFromSearchParams(search: URLSearchParams): boolean {
  return search.get('only_used') === 'true';
}

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: true,
  disableColumnMenu: true,
  flex: 1,
};

interface Props {
  voucherId?: string;
  /** Omit page chrome and avoid syncing filters to the URL (use when embedding on another route). */
  embedded?: boolean;
  /** Default `voucher_user_reporting_tag` when the URL has none (standalone) or for embedded mode. */
  defaultVoucherUserReportingTag?: string;
  /**
   * When `embedded`, voucher users and voucher logs are in accordions; this sets their initial expanded state.
   * Voucher Lookup (standalone) uses the default `true`; voucher auto-assign rule embed uses `false`.
   */
  defaultAccordionsOpen?: boolean;
  /** Hide voucher logs (e.g. when embedded on the voucher edit page). */
  hideVoucherLogs?: boolean;
  /** Hide the page footer debug modal. */
  hideFooterDebug?: boolean;
  /** Show per-row expire action and confirmation dialog (voucher detail / edit). */
  showExpireVoucherUserActions?: boolean;
  /** Disable expire actions while parent form is busy. */
  expireVoucherUserActionsDisabled?: boolean;
  /**
   * Initial "Include expired" when the URL has no `include_expired` param.
   * Voucher edit (direct `voucherId` prop, no URL sync) should pass `true` for prior behavior.
   */
  defaultIncludeExpired?: boolean;
  /**
   * When `onPromoRequestsUserFilterChange` is set, user ID filter is owned by the parent (voucher edit page)
   * so promo requests render once below the fold instead of duplicating inside the users section.
   */
  promoRequestsUserFilter?: string;
  onPromoRequestsUserFilterChange?: (userId: string) => void;
}

export default function VoucherUsersSearchPage({
  voucherId: propVoucherId,
  embedded = false,
  defaultVoucherUserReportingTag = '',
  defaultAccordionsOpen = true,
  hideVoucherLogs = false,
  hideFooterDebug = false,
  showExpireVoucherUserActions = false,
  expireVoucherUserActionsDisabled = false,
  defaultIncludeExpired = false,
  promoRequestsUserFilter,
  onPromoRequestsUserFilterChange,
}: Props = {}) {
  const query = useQuery();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { replace } = useAppNavigation();
  const tenant = useCurrentTenant();
  const { promoMeta } = usePromoMeta();
  const voucherIdFromQuery = embedded ? '' : (query.get('voucher_id') ?? '');
  const hasDirectVoucherId = !!propVoucherId || !!voucherIdFromQuery;
  const isPromoUserFilterLinked = Boolean(onPromoRequestsUserFilterChange);

  const initialReportingTag = embedded
    ? defaultVoucherUserReportingTag || ''
    : query.get('voucher_user_reporting_tag') || defaultVoucherUserReportingTag || '';

  const [pageNum, setPageNum] = useState<number>(
    embedded ? 0 : query.get('page_num') ? Number(query.get('page_num')) : 0,
  );
  const [pageSize, setPageSize] = useState<number>(
    embedded ? 20 : query.get('page_size') ? Number(query.get('page_size')) : 20,
  );
  const [filterUserIdInternal, setFilterUserIdInternal] = useState<string>(embedded ? '' : query.get('user_id') || '');
  const filterUserId = isPromoUserFilterLinked ? (promoRequestsUserFilter ?? '') : filterUserIdInternal;
  const [filterVoucherName, setFilterVoucherName] = useState<string>(embedded ? '' : query.get('voucher_name') || '');
  const [filterReportingTag, setFilterReportingTag] = useState<string>(initialReportingTag);
  const [selectedMatchedVoucherId, setSelectedMatchedVoucherId] = useState<string | undefined>(undefined);
  const [searchWidgetValue, setSearchWidgetValue] = useState<string>(embedded ? '' : query.get('user_id') || '');
  const [filterIncludeExpired, setFilterIncludeExpired] = useState<boolean>(() =>
    embedded ? defaultIncludeExpired : initialIncludeExpiredFromSearchParams(query, defaultIncludeExpired),
  );
  const [filterIncludeUsed, setFilterIncludeUsed] = useState<boolean>(() =>
    embedded ? true : parseIncludeUsedFromSearchParams(query),
  );
  const [filterOnlyUsed, setFilterOnlyUsed] = useState<boolean>(() =>
    embedded ? false : parseOnlyUsedFromSearchParams(query),
  );

  const [expireTarget, setExpireTarget] = useState<VoucherUserRow | null>(null);
  const expireVoucherUserMutation = useExpireVoucherUser();

  const reportingTagMetaOptions = useMemo(
    () => reportingTagOptionsFromPromoMeta(promoMeta?.global_promo_config?.voucherUserReportingTagsConfig),
    [promoMeta?.global_promo_config?.voucherUserReportingTagsConfig],
  );

  /** Tag string → admin label from promo meta (`short_description`), when configured. */
  const voucherUserReportingTagLabelByTag = useMemo(() => {
    const map = new Map<string, string>();
    reportingTagMetaOptions.forEach((o) => {
      map.set(o.value, o.label);
    });
    return map;
  }, [reportingTagMetaOptions]);

  const reportingTagFilterOptions = useMemo(
    () => getReportingTagFilterOptions(reportingTagMetaOptions, filterReportingTag),
    [filterReportingTag, reportingTagMetaOptions],
  );

  const updateFilterUserId = useCallback(
    (next: string) => {
      if (isPromoUserFilterLinked) {
        onPromoRequestsUserFilterChange!(next);
      } else {
        setFilterUserIdInternal(next);
      }
    },
    [isPromoUserFilterLinked, onPromoRequestsUserFilterChange],
  );

  useEffect(() => {
    if (isPromoUserFilterLinked) {
      setSearchWidgetValue(promoRequestsUserFilter ?? '');
    }
  }, [isPromoUserFilterLinked, promoRequestsUserFilter]);

  const handleUserSelected = useCallback(
    (_id: string, value: string) => {
      updateFilterUserId(value);
      setPageNum(0);
    },
    [updateFilterUserId],
  );

  const handleUserIdClick = useCallback(
    (userId: string) => {
      updateFilterUserId(userId);
      setSearchWidgetValue(userId);
      setPageNum(0);
    },
    [updateFilterUserId],
  );

  const handleVoucherClick = useCallback((voucherName: string) => {
    setFilterVoucherName(voucherName);
    setSelectedMatchedVoucherId(undefined);
    setPageNum(0);
  }, []);

  const handleClear = useCallback(() => {
    updateFilterUserId('');
    setFilterVoucherName('');
    setFilterReportingTag(embedded ? defaultVoucherUserReportingTag || '' : '');
    setSelectedMatchedVoucherId(undefined);
    setSearchWidgetValue('');
    setFilterIncludeExpired(false);
    setFilterIncludeUsed(true);
    setFilterOnlyUsed(false);
    setPageNum(0);
    if (!propVoucherId && !embedded) {
      replace({
        search: qs.stringify({
          page_num: '0',
          page_size: pageSize.toString(),
        }),
      });
    }
  }, [defaultVoucherUserReportingTag, embedded, replace, pageSize, propVoucherId, updateFilterUserId]);

  // Name-based voucher lookup — skipped when a direct voucherId is provided
  const { data: matchedVouchersData } = useTanstackQuery({
    queryKey: ['vouchers', 'name-lookup', filterVoucherName, tenant.tenant.brand],
    queryFn: async () =>
      listVouchers({ voucher_name: filterVoucherName, limit: 100, brand: toPromoBrand(tenant.tenant.brand) }),
    enabled: !hasDirectVoucherId && filterVoucherName.length >= 2,
  });

  const matchedVoucherIds = useMemo<Array<string>>(() => {
    if (hasDirectVoucherId || !filterVoucherName || !matchedVouchersData?.result?.vouchers) {
      return [];
    }
    return matchedVouchersData.result.vouchers.map((v) => v.voucherId);
  }, [hasDirectVoucherId, filterVoucherName, matchedVouchersData]);

  const isVoucherNameFilterActive = !hasDirectVoucherId && filterVoucherName.length >= 2;
  const hasResolvedVouchers = matchedVoucherIds.length > 0;
  const isMultiVoucherMatch = !hasDirectVoucherId && matchedVoucherIds.length > 1;
  const hasSelectedFromMultiMatch =
    isMultiVoucherMatch && !!selectedMatchedVoucherId && matchedVoucherIds.includes(selectedMatchedVoucherId);
  const needsDisambiguation = isMultiVoucherMatch && !hasSelectedFromMultiMatch;

  const resolvedVoucherId =
    propVoucherId ??
    (voucherIdFromQuery ? voucherIdFromQuery : undefined) ??
    selectedMatchedVoucherId ??
    (matchedVoucherIds.length === 1 ? matchedVoucherIds[0] : undefined);

  const resolvedVoucher = useMemo<Voucher | undefined>(() => {
    if (!resolvedVoucherId || hasDirectVoucherId) {
      return undefined;
    }
    return matchedVouchersData?.result?.vouchers?.find((v) => v.voucherId === resolvedVoucherId);
  }, [resolvedVoucherId, hasDirectVoucherId, matchedVouchersData]);

  const queryParams: ListAllVoucherUsersQuery = useMemo(
    () => ({
      page: pageNum,
      limit: pageSize,
      includeExpired: filterIncludeExpired,
      ...(filterOnlyUsed ? {} : { includeUsed: filterIncludeUsed }),
      ...(filterOnlyUsed ? { onlyUsed: true } : {}),
      ...(filterUserId ? { user_id: filterUserId } : {}),
      ...(resolvedVoucherId ? { voucher_id: resolvedVoucherId } : {}),
      ...(filterReportingTag ? { voucher_user_reporting_tag: filterReportingTag } : {}),
    }),
    [
      pageNum,
      pageSize,
      filterIncludeExpired,
      filterIncludeUsed,
      filterOnlyUsed,
      filterUserId,
      resolvedVoucherId,
      filterReportingTag,
    ],
  );

  const { data: queryData, isLoading } = useListAllVoucherUsers({
    query: queryParams,
    enabled:
      hasDirectVoucherId ||
      !isVoucherNameFilterActive ||
      (hasResolvedVouchers && !needsDisambiguation) ||
      !filterVoucherName,
  });

  const voucherUsers = useMemo(() => {
    if (isVoucherNameFilterActive && (!hasResolvedVouchers || needsDisambiguation)) {
      return [];
    }
    return queryData?.result?.voucherUsers ?? [];
  }, [isVoucherNameFilterActive, hasResolvedVouchers, needsDisambiguation, queryData]);

  const totalCount = useMemo(() => {
    if (isVoucherNameFilterActive && (!hasResolvedVouchers || needsDisambiguation)) {
      return 0;
    }
    return Number(queryData?.result?.total ?? 0);
  }, [isVoucherNameFilterActive, hasResolvedVouchers, needsDisambiguation, queryData]);

  const brand = useCurrentPromoBrand();

  const uniqueUserIds = useMemo(() => {
    return Array.from(new Set(voucherUsers.map((vu) => vu.userId).filter(Boolean))).sort();
  }, [voucherUsers]);

  const { data: usersSummaryMap } = useGetUsersSummaryByIds({
    userIds: uniqueUserIds,
    brand: brand.brand,
  });
  const resolvedSummaryMap = usersSummaryMap ?? new Map();

  const uniqueVoucherIds = useMemo(() => {
    return Array.from(new Set(voucherUsers.map((vu) => vu.voucherId).filter(Boolean))).sort();
  }, [voucherUsers]);

  const { data: vouchersMap } = useGetVouchersByIds({ voucherIds: uniqueVoucherIds, brand: tenant.tenant.brand });
  const resolvedVouchersMap = vouchersMap ?? new Map();

  const syncQueryParams = useCallback(() => {
    if (hasDirectVoucherId || embedded) {
      return;
    }
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      user_id: filterUserId || undefined,
      voucher_name: filterVoucherName || undefined,
      voucher_user_reporting_tag: filterReportingTag || undefined,
      ...(filterIncludeExpired ? { include_expired: 'true' } : {}),
      ...(!filterIncludeUsed && !filterOnlyUsed ? { include_used: 'false' } : {}),
      ...(filterOnlyUsed ? { only_used: 'true' } : {}),
    });
    replace({ search: `?${searchParams}` });
  }, [
    embedded,
    filterIncludeExpired,
    filterIncludeUsed,
    filterOnlyUsed,
    hasDirectVoucherId,
    replace,
    pageNum,
    pageSize,
    filterUserId,
    filterVoucherName,
    filterReportingTag,
  ]);

  useEffect(() => {
    syncQueryParams();
  }, [syncQueryParams]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const [voucherLogsQuery, setVoucherLogsQuery] = useState<ListVoucherLogsQuery>({});

  const firstMatchedVoucherId = useMemo(() => {
    if (propVoucherId) {
      return propVoucherId;
    }
    if (voucherIdFromQuery) {
      return voucherIdFromQuery;
    }
    if (selectedMatchedVoucherId) {
      return selectedMatchedVoucherId;
    }
    if (!filterVoucherName || matchedVoucherIds.length === 0) {
      return undefined;
    }
    return matchedVoucherIds[0];
  }, [propVoucherId, voucherIdFromQuery, selectedMatchedVoucherId, filterVoucherName, matchedVoucherIds]);

  const voucherLogsQueryOptions = useMemo<ListVoucherLogsQuery>(
    () => ({
      ...voucherLogsQuery,
      ...(filterUserId ? { userId: filterUserId } : {}),
      ...(firstMatchedVoucherId ? { voucherId: firstMatchedVoucherId } : {}),
      ...(brand.promoBrand ? { brand: brand.promoBrand } : { brand: 'luxuryescapes' }),
    }),
    [voucherLogsQuery, filterUserId, firstMatchedVoucherId, brand],
  );

  const voucherLogsListQueryResolved = useMemo<ListVoucherLogsQuery>(
    () => ({
      ...VOUCHER_LOGS_TABLE_DEFAULT_QUERY,
      ...voucherLogsQueryOptions,
    }),
    [voucherLogsQueryOptions],
  );

  const { data: voucherLogsListSummary } = useListVoucherLogs({
    query: voucherLogsListQueryResolved,
    enabled: !hideVoucherLogs,
  });

  const voucherLogsTotalCount = Number(voucherLogsListSummary?.result?.total ?? 0);
  const voucherLogsPageRowCount = voucherLogsListSummary?.result?.logs?.length ?? 0;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    updatedAt: false,
  });
  const [extendedInfo, setExtendedInfo] = useState(false);

  const handleColumnVisibilityChange = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
  }, []);

  const shouldShowDeleteVoucherUserActions = showExpireVoucherUserActions || !embedded;

  const mergedColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      ...columnVisibilityModel,
      voucherUserReportingTags: extendedInfo,
      fkCauseId: extendedInfo,
      debug: extendedInfo,
      ...(shouldShowDeleteVoucherUserActions ? { expireActions: true } : {}),
    }),
    [columnVisibilityModel, extendedInfo, shouldShowDeleteVoucherUserActions],
  );

  const handleGridColumnVisibilityModelChange = useCallback((model: GridColumnVisibilityModel) => {
    const { voucherUserReportingTags: _t, fkCauseId: _c, debug: _d, expireActions: _e, ...rest } = model;
    setColumnVisibilityModel(rest);
  }, []);

  const handleExpireConfirm = useCallback(async () => {
    if (!expireTarget) {
      return;
    }
    try {
      await expireVoucherUserMutation.mutateAsync(expireTarget.voucherUserId);
      enqueueSnackbar('Deleted voucher user', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
      if (resolvedVoucherId) {
        queryClient.invalidateQueries({ queryKey: ['voucher-users', resolvedVoucherId] });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to delete voucher user: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setExpireTarget(null);
    }
  }, [enqueueSnackbar, expireTarget, expireVoucherUserMutation, queryClient, resolvedVoucherId]);

  const voucherUsersSearchPageDebugData = useMemo(
    () => ({
      queryParams,
      resolvedVoucherId,
      filterUserId: filterUserId || undefined,
      filterVoucherName: filterVoucherName || undefined,
      filterReportingTag: filterReportingTag || undefined,
      totalCount,
      voucherUsers,
      listAllVoucherUsersResponse: queryData,
      tenant: tenant.tenant,
      hasDirectVoucherId,
      needsDisambiguation,
    }),
    [
      filterReportingTag,
      filterUserId,
      filterVoucherName,
      hasDirectVoucherId,
      needsDisambiguation,
      queryData,
      queryParams,
      resolvedVoucherId,
      tenant.tenant,
      totalCount,
      voucherUsers,
    ],
  );

  const expireTargetUserName = expireTarget
    ? (resolvedSummaryMap.get(expireTarget.userId)?.full_name ?? expireTarget.userId)
    : '';
  const expireTargetVoucherName = expireTarget
    ? (resolvedVouchersMap.get(expireTarget.voucherId)?.voucherName ?? expireTarget.voucherId)
    : '';

  const columns = useMemo<Array<GridColDef<VoucherUserRow>>>(() => {
    const base: Array<GridColDef<VoucherUserRow>> = [
      {
        field: 'userName',
        headerName: 'Name',
        ...sharedOpts,
        renderCell: (params) => (
          <Stack spacing={0.5} alignItems="center">
            {resolvedSummaryMap.get(params.row.userId)?.full_name ?? '-'}
            <Typography variant="body2" color="text.secondary">
              {resolvedSummaryMap.get(params.row.userId)?.email ?? '-'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'userId',
        headerName: 'User ID',
        ...sharedOpts,
        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={() => handleUserIdClick(params.value)}
            sx={{ textTransform: 'none' }}
          >
            {params.value}
          </Button>
        ),
      },
      {
        field: 'voucherId',
        headerName: 'Voucher',
        ...sharedOpts,
        renderCell: (params) => {
          const voucher = resolvedVouchersMap.get(params.row.voucherId);
          const voucherName = voucher?.voucherName ?? params.row.voucherId;
          return (
            <CopyableField
              value={voucherName}
              label={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleVoucherClick(voucher?.voucherName ?? '')}
                  sx={{ textTransform: 'none' }}
                >
                  {voucherName}
                </Button>
              }
            />
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedOpts,
        renderCell: (params) => DateFormatterDetailed({ date: params.value }),
      },
      {
        field: 'usedAt',
        headerName: 'Used At',
        ...sharedOpts,
        renderCell: (params) => (params.value ? DateFormatterDetailed({ date: params.value, includeYear: true }) : '-'),
      },
      {
        field: 'expiredAt',
        headerName: 'Expires At',
        ...sharedOpts,
        renderCell: (params) =>
          params.value ? DateFormatterDetailed({ date: params.value, highlightIfPast: true, includeYear: true }) : '-',
      },
      {
        field: 'voucherUserReportingTags',
        headerName: 'Reporting Tags',
        ...sharedOpts,
        minWidth: 200,
        renderCell: (params) => {
          const tags = params.row.voucherUserReportingTags;
          if (!tags?.length) {
            return '—';
          }
          return (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ py: 0.5, justifyContent: 'center' }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  variant="outlined"
                  label={voucherUserReportingTagLabelByTag.get(tag) ?? tag}
                  title={tag}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'fkCauseId',
        headerName: 'Cause ID',
        ...sharedOpts,
        renderCell: (params) => {
          const causeId = params.row.fkCauseId ?? params.row.idCauseFk;
          if (!causeId) {
            return '—';
          }
          const isAutoAssignOrder = params.row.voucherUserReportingTags?.includes(
            VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER,
          );
          if (isAutoAssignOrder) {
            return (
              <Link to={`/purchases/${causeId}`} target="_blank" rel="noreferrer">
                {causeId}
              </Link>
            );
          }
          return (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {causeId}
            </Typography>
          );
        },
      },
      {
        field: 'debug',
        headerName: 'Debug',
        ...sharedOpts,
        renderCell: (params) => {
          const userSummary = resolvedSummaryMap.get(params.row.userId);
          return (
            <DebugModal
              type="generic"
              title="Voucher User"
              data={{
                ...params.row,
                userName: userSummary?.full_name,
                userEmail: userSummary?.email,
              }}
            />
          );
        },
      },
    ];

    if (shouldShowDeleteVoucherUserActions) {
      base.push({
        field: 'expireActions',
        headerName: '',
        width: 90,
        ...sharedOpts,
        hideable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            aria-label="Delete voucher user"
            size="small"
            onClick={() => setExpireTarget(params.row)}
            disabled={expireVoucherUserActionsDisabled || expireVoucherUserMutation.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      });
    }

    return base;
  }, [
    resolvedSummaryMap,
    resolvedVouchersMap,
    handleUserIdClick,
    handleVoucherClick,
    voucherUserReportingTagLabelByTag,
    shouldShowDeleteVoucherUserActions,
    expireVoucherUserActionsDisabled,
    expireVoucherUserMutation.isPending,
  ]);

  const columnsForSelector = useMemo(
    () =>
      columns.filter(
        (c) =>
          c.field !== 'debug' &&
          c.field !== 'voucherUserReportingTags' &&
          c.field !== 'fkCauseId' &&
          c.field !== 'expireActions',
      ),
    [columns],
  );

  const Toolbar = useCallback(
    () => (
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ p: 1, width: '100%', justifyContent: 'flex-end' }}
      >
        <FormControlLabel
          control={<Switch checked={extendedInfo} onChange={(_, checked) => setExtendedInfo(checked)} size="small" />}
          label="Extended info"
        />
        <ColumnSelector
          columns={columnsForSelector}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          size="small"
        />
      </Stack>
    ),
    [columnVisibilityModel, columnsForSelector, extendedInfo, handleColumnVisibilityChange],
  );

  const voucherLookupMainSections = (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <UserSearchWidget
            id={embedded ? 'voucher-users-search-embedded' : 'voucher-users-search'}
            label="Name / Email / Customer Support Code / User ID"
            formErrors={[]}
            onChange={handleUserSelected}
            passedAutocompleteValue={searchWidgetValue}
            setPassedAutocompleteValue={setSearchWidgetValue}
          />
        </Box>
        {!hasDirectVoucherId && (
          <TextField
            label="Voucher Name"
            size="small"
            value={filterVoucherName}
            onChange={(e) => {
              setFilterVoucherName(e.target.value);
              setSelectedMatchedVoucherId(undefined);
              setPageNum(0);
            }}
            sx={{ minWidth: 220 }}
          />
        )}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="voucher-user-reporting-tag-filter-label" shrink>
            Reporting tag
          </InputLabel>
          <Select
            labelId="voucher-user-reporting-tag-filter-label"
            label="Reporting tag"
            displayEmpty
            value={filterReportingTag}
            onChange={(e) => {
              setFilterReportingTag(e.target.value);
              setPageNum(0);
            }}
            renderValue={(selected) => reportingTagFilterOptions.find((o) => o.value === selected)?.label ?? selected}
          >
            {reportingTagFilterOptions.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filterIncludeExpired}
              onChange={(_, checked) => {
                setFilterIncludeExpired(checked);
                setPageNum(0);
              }}
            />
          }
          label="Include expired"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filterIncludeUsed}
              onChange={(_, checked) => {
                setFilterIncludeUsed(checked);
                setPageNum(0);
              }}
            />
          }
          label="Include used"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filterOnlyUsed}
              onChange={(_, checked) => {
                setFilterOnlyUsed(checked);
                setPageNum(0);
              }}
            />
          }
          label="Only used"
        />
        <Button variant="text" onClick={handleClear}>
          Clear
        </Button>
      </Stack>

      {needsDisambiguation && matchedVouchersData?.result?.vouchers && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Multiple vouchers match &ldquo;{filterVoucherName}&rdquo;</AlertTitle>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {matchedVouchersData.result.vouchers.map((v) => (
              <Chip
                key={v.voucherId}
                label={v.voucherName}
                onClick={() => setSelectedMatchedVoucherId(v.voucherId)}
                variant="outlined"
                clickable
              />
            ))}
          </Stack>
        </Alert>
      )}

      {resolvedVoucher && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Stack direction="row" spacing={4} alignItems="flex-start" flexWrap="wrap">
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">
                Voucher
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                <Link to={`/marketing/vouchers/${resolvedVoucher.voucherId}`}>{resolvedVoucher.voucherName}</Link>
              </Typography>
              {resolvedVoucher.voucherTitle && resolvedVoucher.voucherTitle !== resolvedVoucher.voucherName && (
                <Typography variant="body2" color="text.secondary">
                  {resolvedVoucher.voucherTitle}
                </Typography>
              )}
            </Box>

            {resolvedVoucher.voucherDescription && (
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">{resolvedVoucher.voucherDescription}</Typography>
              </Box>
            )}

            {resolvedVoucher.voucherRewards && resolvedVoucher.voucherRewards.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Rewards
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {resolvedVoucher.voucherRewards.map((r) => (
                    <Chip
                      key={r.id_voucher_reward}
                      size="small"
                      label={
                        r.currency_code ? currencyFormatter(r.currency_code, r.reward_amount) : `${r.reward_amount}`
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box>
                <Chip
                  size="small"
                  color={resolvedVoucher.deactivatedAt ? 'error' : 'success'}
                  label={resolvedVoucher.deactivatedAt ? 'Deactivated' : 'Active'}
                />
              </Box>
            </Box>

            {resolvedVoucher.voucherToggles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Toggles
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {resolvedVoucher.voucherToggles.map((t) => (
                    <Chip key={t} size="small" variant="outlined" label={t} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination, toolbar: Toolbar }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={voucherUsers}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          getRowId={(row) => row.voucherUserId}
          paginationModel={{ page: pageNum, pageSize }}
          columnVisibilityModel={mergedColumnVisibilityModel}
          onColumnVisibilityModelChange={handleGridColumnVisibilityModelChange}
          sx={{ backgroundColor: 'background.paper' }}
        />
      </Box>

      {filterUserId && !isPromoUserFilterLinked && (
        <VoucherRecentRequests
          voucherName={filterVoucherName}
          userId={filterUserId}
          brand={tenant.tenant.brand}
          promoMeta={promoMeta}
          showVoucherLogsAccordion={false}
        />
      )}
    </>
  );

  const voucherLogsSection = (
    <VoucherLogsTable
      queryOptions={voucherLogsQueryOptions}
      onQueryOptionsChange={setVoucherLogsQuery}
      onUserIdClick={handleUserIdClick}
      onVoucherClick={handleVoucherClick}
      flushTop={embedded}
    />
  );

  const content = (
    <Box>
      {embedded ? (
        <>
          <Accordion
            defaultExpanded={defaultAccordionsOpen}
            disableGutters
            elevation={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{`Voucher users [${voucherUsers.length} of ${totalCount}]`}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>{voucherLookupMainSections}</AccordionDetails>
          </Accordion>

          {!hideVoucherLogs && (
            <Accordion
              defaultExpanded={defaultAccordionsOpen}
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{`Voucher logs [${voucherLogsPageRowCount} of ${voucherLogsTotalCount}]`}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>{voucherLogsSection}</AccordionDetails>
            </Accordion>
          )}
        </>
      ) : (
        <>
          <PageSubheader title={`Voucher Users [${voucherUsers.length} of ${totalCount}]`} />
          {voucherLookupMainSections}
          {!hideVoucherLogs && voucherLogsSection}
        </>
      )}
      <Dialog open={Boolean(expireTarget)} onClose={() => setExpireTarget(null)}>
        <DialogTitle>Delete Voucher User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove voucher {expireTargetVoucherName} for user {expireTargetUserName}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setExpireTarget(null)} disabled={expireVoucherUserMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExpireConfirm}
            disabled={expireVoucherUserMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {!hideFooterDebug && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <DebugModal type="generic" title="Voucher users search page debug" data={voucherUsersSearchPageDebugData} />
        </Box>
      )}
    </Box>
  );

  if (hasDirectVoucherId || embedded) {
    return content;
  }

  return (
    <Container maxWidth="xl" sx={{ width: '90%' }}>
      <Helmet>
        <title>Voucher Lookup</title>
      </Helmet>
      {content}
    </Container>
  );
}
